import { withNamespaces } from "react-i18next";
import React, { useState, memo, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { connect } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
	PaginationProvider,
	PaginationListStandalone,
	SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import axios from "axios";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { 
	Row, 
	Col, 
	Alert, 
	Label
} from "reactstrap";
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'react-bootstrap'
import { withRouter } from "react-router-dom";
import UserUtils from "src/services/utils/UserUtils";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import {
	fetchUsers,
	fetchCounts,
	updateUserStatus,
    resendInvitationEmail,
} from "../../api/apiFunctions";
import { getTableColumns } from "./user-table-columns";
import { routes } from "../../data";
import DeleteUserModal from "../modal/DeleteUserModal";
import FilteringComponent from "../filtering-component";
import DownloadTemplateButton from "../download-button"
import ConfirmUserStatusModal from "../modal/ConfirmUserStatusModal";
import ResendInvitationEmailModal from "../modal/ResendInvitationEmailModal";
import UploadCSVModal from "../modal/UploadCSVModal";

const UserTable = (props) => {
	const { t, user } = props;
	const activeModule = props.modules?.active;
	const [userIdToDelete, setUserIdToDelete] = useState(null);
    const [ userIdToSendEmail, setUserIdToSendEmail ] = useState(null);
    const [ userToChangeStatus, setUserToChangeStatus ] = useState(null);
	const [ moreActionsDropDownOpen, setMoreActionsDropDownOpen ] = useState(false)
    const [ showUploadCSVModal, setShowUploadCSVModal ] = useState(false);
	
	const [filters, setFilters] = useState({
		pageIndex: 1,
		pageSize: 25,
		sortField: null,
		sortOrder: null,
	});

	const toggleMoreActionsDropDown = () => setMoreActionsDropDownOpen(!moreActionsDropDownOpen)

	const userUtils = new UserUtils(user);
	const getModuleRoutes = (activeModule) => routes[activeModule];

	const updateUserStatusHandler = (user) => {
        setUserToChangeStatus(user)
	};

	const tableColumns = getTableColumns({
		t,
		userUtils,
		setUserIdToDelete,
        setUserIdToSendEmail,
		user,
		activeModule,
		updateUserStatusHandler,
	});

	const handleFetchUsersQuery = useQuery({
		queryKey: ["user-management-fetch-list", filters],
		queryFn: async () => {
			return await fetchUsers({
				token: props.token,
				activeModule,
				payload: filters,
			});
		},
		refetchOnWindowFocus: false,
		onError: () => {
			toast(t("An error occurred while fetching user list."), {
				type: "error",
			});
		}
	});

	const handleFetchUsersCountQuery = useQuery({
		queryKey: ["user-management-fetch-count-by-role"],
		queryFn: async () => {
			return await fetchCounts({
				token: props.token,
				activeModule,
			});
		},
		refetchOnWindowFocus: false,
		onError: () => {
			toast(t("An error occurred while fetching user counts."), {
				type: "error",
			});
		}
	});

	const updateUserStatusMutation = useMutation({
		mutationFn: async ({ userId, nextStatus }) => {
			await updateUserStatus({
				activeModule,
				token: props.token,
				userId,
				payload: { status: nextStatus },
			});
		},
		onSuccess: () => {
			toast(props.t("User status updated successfully"), {
				type: "success",
			});
			handleFetchUsersQuery.refetch();
		},
		onError: (error) => {
			if (axios.isAxiosError(error) && error.response) {
				const data = error.response.data;
				toast(props.t(data.message), { type: "error" });
			} else {
				toast(props.t("An error occurred while updating the user status"), {
				type: "error",
				});
			}
		}
	});

    const resendInvitationEmailMutation = useMutation({
		mutationFn: async (userId) => {
			await resendInvitationEmail({
				activeModule,
				token: props.token,
				userId
			});
		},
		onSuccess: () => {
			toast(props.t("Invitation email has been sent successfully to the user."), {
				type: "success",
			});
		},
		onError: () => {
            toast(props.t("Failed to send the invitation email. Please try again."), {
                type: "error"
            });
		}
	});

	const NoDataIndication = () =>
		handleFetchUsersQuery.isFetched && !handleFetchUsersQuery.length ? (
			<div className="alert m-0" role="alert">
				<p
					style={{
						textAlign: "center",
						marginBottom: 0,
					}}
				>
					{props.t('This list is empty')}
				</p>
			</div>
		) : (
			<></>
		);

    const resendInvitationEmailHandler = () => {
        resendInvitationEmailMutation.mutate(userIdToSendEmail, {
            onSettled: () => {
                setUserIdToSendEmail(null)
            }
        });
    }

    const handleTableChange = useCallback((type, options) => {
        const {
			page,
			sizePerPage,

			sortField,
			sortOrder,
		} = options;

		switch (type) {
		case "pagination":
                setFilters((currentState) => ({
                    ...currentState,
                    pageIndex: page,
					pageSize: sizePerPage
                }));
			break;

		case "sort":
                setFilters((currentState) => ({
                    ...currentState,
                    sortField: sortField,
					sortOrder: sortOrder.toUpperCase()
                }));
			break;

		default:
			return false;
		}
    }, []);

    const handleFilterOnChange = useCallback((newFilters) => {
        setFilters((currentState) => ({
            ...currentState,
            ...newFilters,
            pageIndex: 1
        }));
    }, []);

    const handleConfirmChangeUserStatus = useCallback((payload) => {
        updateUserStatusMutation.mutate(payload, {
            onSettled: () => {
                setUserToChangeStatus(null)
            }
        })
    }, [
        updateUserStatusMutation.mutate
    ]);

	return (
		<div className="p-4" style={{ marginBottom: "300px" }}>
            <DeleteUserModal
                userIdToDelete={userIdToDelete}
                isOpen={!!userIdToDelete}
                close={() => setUserIdToDelete(null)}
                onComplete={() => {
                    setUserIdToDelete(null);
                    handleFetchUsersQuery.refetch();
                    handleFetchUsersCountQuery.refetch();
                }}
            />

            <ConfirmUserStatusModal
                isOpen={!!userToChangeStatus}
                toggle={() => setUserToChangeStatus(null)}
                onConfirm={handleConfirmChangeUserStatus}
                user={userToChangeStatus}
                isLoading={updateUserStatusMutation.isLoading}
            />

            <ResendInvitationEmailModal 
                isOpen={!!userIdToSendEmail}
                closeModal={() => setUserIdToSendEmail(null)}
                onConfirm={resendInvitationEmailHandler}
                isLoading={resendInvitationEmailMutation.isLoading}
            />

            <UploadCSVModal 
                isOpen={showUploadCSVModal}
                toggle={() => setShowUploadCSVModal(false)}
                onUpload={() => {
                    setShowUploadCSVModal(false);
                    handleFetchUsersQuery.refetch();
                    handleFetchUsersCountQuery.refetch();
                }}
            />

			{isFeatureEnabledForActiveModule("list-users") ? (
				<PaginationProvider
					pagination={paginationFactory({
						custom: true,
						page: filters.pageIndex,
						sizePerPage: filters.pageSize,
						totalSize: handleFetchUsersQuery?.data?.itemsCount || 0,
						withFirstAndLast: false,
						alwaysShowAllBtns: true,
						prePageText: (
							<span>
								<i className="ri-arrow-left-s-line"></i> {props.t("Back")}
							</span>
						),
						nextPageText: (
							<span>
								{props.t("Next")} <i className="ri-arrow-right-s-line"></i>
							</span>
						),
						prePageTitle: props.t("Pre page"),
						firstPageTitle: props.t("Next page"),
						showTotal: true,
						hideSizePerPage: false,
						sizePerPageList: [
							{
								text: "25",
								value: 25,
							},
							{
								text: "50",
								value: 50,
							}
						],
					})}
				>
					{({ paginationProps, paginationTableProps }) => {
						return (
							<React.Fragment>
								<Row className="mb-3 mt-3">
                                    <Col sm="12" md="6" className="d-flex align-items-start justify-content-start">
                                        {!handleFetchUsersCountQuery?.isFetching ? (
                                            <div>
                                                <Label
                                                    hidden={!userUtils?.isSuperAdmin(user)}
                                                    className="me-4"
                                                >
                                                    {`${t("Analyst Admins/Analysts")} : ${handleFetchUsersCountQuery?.data
                                                            ?.registeredAnalysts || 0
                                                        }/${handleFetchUsersCountQuery?.data
                                                            ?.analystLicenseCount || 0
                                                        }`}
                                                </Label>

                                                <Label
                                                    hidden={activeModule === "tp"}
                                                    className="me-4"
                                                >{`${t("Task Owners")} : ${handleFetchUsersCountQuery?.data
                                                        ?.registeredTaskOwners || 0
                                                    }/${handleFetchUsersCountQuery?.data
                                                        ?.taskOwnerLicenseCount || 0
                                                    }`}</Label>
                                            </div>
                                        ) : (
                                            <div className="dt-field dt-skeleton dt-select-list me-4"></div>
                                        )}
                                    </Col>

                                    <Col sm="12" md="6" className="d-flex align-items-center justify-content-end">
                                        <FilteringComponent
                                            onChange={handleFilterOnChange}
                                        />

                                        <Dropdown
                                            isOpen={moreActionsDropDownOpen}
                                            toggle={toggleMoreActionsDropDown}
                                        >
                                            <DropdownToggle
                                                caret
                                                variant="outline-secondary"
                                                outline
                                            >
                                                {props.t('Actions')}
                                            </DropdownToggle>
                                            <DropdownMenu
                                                className='dropdown-menu-end'
                                            >
                                                {isFeatureEnabledForActiveModule("ad-settings") && (
                                                    <DropdownItem
                                                        onClick={() => props.history.push('/admin/administration/users/ad-settings')}
                                                    >
                                                        <i className="ri-settings-4-line align-middle me-2" />
                                                        {props.t("AD settings")}
                                                    </DropdownItem>
                                                )}

                                                {!(
                                                    activeModule === "tp" && !userUtils?.isSuperAdmin(user)
                                                ) && (
                                                        <>
                                                            {isFeatureEnabledForActiveModule("new-user") && (
                                                                <DropdownItem
                                                                    onClick={() => props.history.push(getModuleRoutes(activeModule).newUser)}
                                                                >
                                                                    <i className="ri-add-line align-middle me-2" />
                                                                    {props.t("New user")}
                                                                </DropdownItem>
                                                            )}

                                                            {isFeatureEnabledForActiveModule("new-user-upload-csv") && (
                                                                <DropdownItem
                                                                    onClick={() => setShowUploadCSVModal(true)}
                                                                >
                                                                    <i className="ri-file-upload-line align-middle me-2" />
                                                                    {props.t("Upload CSV")}
                                                                </DropdownItem>
                                                            )}

                                                            <DropdownItem
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()
                                                                }}
                                                            >
                                                                <DownloadTemplateButton />
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                            </DropdownMenu>
                                        </Dropdown>
                                    </Col>
								</Row>

								<Row>
									<Col sm="12">
										<BootstrapTable
											remote={{
												pagination: true,
												filter: false,
												sort: true,
												cellEdit: false,
												search: false,
											}}
											loading={
												handleFetchUsersQuery.isFetching ||
												handleFetchUsersQuery.isLoading
											}
											overlay={overlayFactory({
												spinner: (
													<Spinner
														animation="border"
														variant="primary"
														size="md"
													/>
												),
												text: "Loading...",
											})}
											onTableChange={handleTableChange}
											defaultSorted={[]}
											keyField={"id"}
											responsive
											bordered={false}
											data={handleFetchUsersQuery?.data?.users || []}
											striped={true}
											columns={tableColumns}
											wrapperClasses="table-responsive"
											classes={"table tpdd-table"}
											headerWrapperClasses={"thead-light"}
											style={{
												overflowX: "auto",
											}}
											noDataIndication={() => <NoDataIndication />}
											{...paginationTableProps}
										/>
									</Col>
								</Row>

								<Row>
									<Col sm="12" md="6">
										<div className="tpdd-pagination-style-1">
											<PaginationListStandalone {...paginationProps} />

											<SizePerPageDropdownStandalone {...paginationProps} />
										</div>
									</Col>
								</Row>
							</React.Fragment>
						);
					}}
				</PaginationProvider>
			) : (
				<Alert className="mb-4" color="warning">
					{props.t("You do not have permission to access these pages")}
				</Alert>
			)}
		</div>
	);
};

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;
	const { Organization, Modules } = state;
	return {
		token,
		user,
		Organization,
		modules: Modules,
	};
};

export default withNamespaces()(
	withRouter(connect(mapStatetoProps)(memo(UserTable)))
);
